'use client';

import { useMemo } from 'react';
import {
  calendarStatus,
  dayResult,
  dayStatus,
  firstResult,
  getStartAndEndOfMonth,
  shiftDay,
  type DayPlayStatus,
  type DayResult,
  type GameType,
  type PlayResult,
  type SimpleDate,
  type SyncedPlay,
} from '@puzzlio/core';
import { GAME_CONFIG, PLAYABLE_GAME_TYPES } from '../games/config';
import { useScopeReady, usePlaysVersion } from '../lib/play-sync/client/PlayScope';
import { challengePlays, playsForDates } from '../lib/play-sync/client/store';

/*
  The player's days, read from the browser's synced copy of their plays (lib/play-sync/client) rather than
  asked of the server: week bars, the archive calendar, the solved screen's next-puzzle ladder and the day
  results page cost no request.

  Every day from a game's first puzzle on has a board, so a day with no plays is 'not-started'; a day before
  the game existed has no entry. Until the player's plays have synced once there's nothing true to draw, so
  `data` is undefined meanwhile (and `isError` if that sync failed).
*/

/** One day of one game, as the week bar and calendar draw it. */
export type DayStatusEntry = {
  date: SimpleDate;
  playStatus: DayPlayStatus;
  /** The first solve's numbers, once there is one; a replay never replaces it. */
  result?: PlayResult;
};

/** Each day of one game from `start` to `end` that had a board, with its plays. */
function dayEntries(
  scope: string,
  gameType: GameType,
  start: SimpleDate,
  end: SimpleDate,
  status: (plays: SyncedPlay[]) => DayPlayStatus = dayStatus
): DayStatusEntry[] {
  const { gameId, earliestDate } = GAME_CONFIG[gameType];
  const byDay = new Map<string, SyncedPlay[]>();
  for (const play of playsForDates(scope, gameId, start, end)) {
    byDay.set(play.boardDate!, [...(byDay.get(play.boardDate!) ?? []), play]);
  }
  const entries: DayStatusEntry[] = [];
  for (let day = start < earliestDate ? earliestDate : start; day <= end; day = shiftDay(day, 1)) {
    const plays = byDay.get(day) ?? [];
    entries.push({ date: day, playStatus: status(plays), result: firstResult(plays) });
  }
  return entries;
}

/** A weekly challenge's key in the map useChallengeStatuses returns. */
export const challengeKey = (gameType: GameType, week: string) => `${gameType}:${week}`;

/**
 * The player's status on each weekly challenge they've played, keyed by challengeKey; a challenge missing
 * from the map hasn't been started. Undefined until the player's plays have synced once.
 */
export function useChallengeStatuses(): Map<string, DayPlayStatus> | undefined {
  const { scope } = useScopeReady();
  const version = usePlaysVersion();
  return useMemo(() => {
    if (!scope) return undefined;
    const byChallenge = new Map<string, SyncedPlay[]>();
    for (const play of challengePlays(scope)) {
      const gameType = PLAYABLE_GAME_TYPES.find((type) => GAME_CONFIG[type].gameId === play.gameId);
      if (!gameType) continue;
      const key = challengeKey(gameType, play.boardWeek!);
      byChallenge.set(key, [...(byChallenge.get(key) ?? []), play]);
    }
    return new Map([...byChallenge].map(([key, plays]) => [key, dayStatus(plays)]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, version]);
}

/** Statuses for every game, keyed by game. */
export type WeekStatusesByGame = Record<GameType, DayStatusEntry[]>;

/** Every playable game's days for one week, for the solved screen's ladder. */
export function useWeekBoardsAndPlays(weekStart: SimpleDate, weekEnd: SimpleDate, options?: { enabled?: boolean }) {
  const { scope, failed } = useScopeReady();
  const version = usePlaysVersion();
  const enabled = options?.enabled ?? true;
  const data = useMemo(
    () =>
      enabled && scope
        ? (Object.fromEntries(
            PLAYABLE_GAME_TYPES.map((gameType) => [gameType, dayEntries(scope, gameType, weekStart, weekEnd)])
          ) as WeekStatusesByGame)
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enabled, scope, weekStart, weekEnd, version]
  );
  return { data, isPending: !data && !failed, isError: failed };
}

/** One game's days for one week, for its week bar. */
export function useWeekStatusesForGame(
  gameType: GameType,
  weekStart: SimpleDate,
  weekEnd: SimpleDate,
  options?: { enabled?: boolean }
) {
  const { scope } = useScopeReady();
  const version = usePlaysVersion();
  const enabled = options?.enabled ?? true;
  const data = useMemo(
    () => (enabled && scope ? dayEntries(scope, gameType, weekStart, weekEnd) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enabled, scope, gameType, weekStart, weekEnd, version]
  );
  return { data };
}

/** One game's days for the month containing `referenceDate`, for the archive calendar, where a reset solve
 * counts as unplayed. */
export function useUserMonthStatus(gameType: GameType, referenceDate: SimpleDate) {
  const { scope } = useScopeReady();
  const version = usePlaysVersion();
  const [monthStart, monthEnd] = getStartAndEndOfMonth(referenceDate);
  const data = useMemo(
    () => (scope ? dayEntries(scope, gameType, monthStart, monthEnd, calendarStatus) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scope, gameType, monthStart, monthEnd, version]
  );
  return { data, isLoading: !data };
}

/** One game's outcome for a single day, as the day-results page reports it. */
export type DayGameResult = DayResult & { gameType: GameType };

/**
 * Every game's outcome for one day. A game that hadn't launched yet on that date is left out rather than
 * shown as having no puzzle.
 */
export function useDayResults(date: SimpleDate) {
  const { scope, failed } = useScopeReady();
  const version = usePlaysVersion();
  const results = useMemo(
    () =>
      scope
        ? PLAYABLE_GAME_TYPES.filter((gameType) => date >= GAME_CONFIG[gameType].earliestDate).map(
            (gameType): DayGameResult => ({
              gameType,
              ...dayResult(playsForDates(scope, GAME_CONFIG[gameType].gameId, date, date)),
            })
          )
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scope, date, version]
  );
  return { results, isPending: !results && !failed, isError: failed };
}
